import {
    FOLDERS_SEPARATOR_BACKEND,
    FOLDERS_SEPARATOR_DISPLAY,
    FOLDERS_SEPARATOR_ESCAPE_WRAPPER_CHARACTER
} from '../../MainMenu/ReportList/Constants'
import { ReportListItemType } from '../../MainMenu/ReportList/Types'

export const getReportNameFromReportDescription = (description: string): string => {
    const filePathSplit = description.split(FOLDERS_SEPARATOR_BACKEND)
    const reportName = filePathSplit[filePathSplit.length - 1]
    return reportName
}

/**
 * Retrieves folderPath part from report description provided by server side.
 *
 * @param description Report description as returned by server side.
 * @return Folder path value as expected by users and front-end application.
 */
export const getFolderFromReportDescription = (description: string): string => {
    const filePathSplit = description.split(FOLDERS_SEPARATOR_BACKEND)

    if (filePathSplit.length > 1) {
        // Make copy of report
        // Trim previous folder option off the end.
        const foldersPartsArray = filePathSplit.splice(0, filePathSplit.length - 1).map((folder: string): string => {
            if (folder.indexOf(FOLDERS_SEPARATOR_DISPLAY) > -1) {
                // If folder name includes \ as a valid character (part of the name), we need to wrap the whole folder name into "".
                return `${FOLDERS_SEPARATOR_ESCAPE_WRAPPER_CHARACTER}${folder}${FOLDERS_SEPARATOR_ESCAPE_WRAPPER_CHARACTER}`
            }
            return folder
        })

        let joinedFolderPath = foldersPartsArray.join(FOLDERS_SEPARATOR_DISPLAY)
        // Add ending slash on all folders.
        joinedFolderPath = `${joinedFolderPath}${FOLDERS_SEPARATOR_DISPLAY}`
        return joinedFolderPath
    }
    return ''
}

/**
 * Parses folderPath value provided by user - handles character escaping and whitespace trimming and prepares folder path for server update.
 *
 * @param folderPath Folder path provided by user.
 * @return Returns folder path as expected by server side.
 */
export const parseFolderPathFromUserAndTransformIntoValueForRequest = (folderPath: string): string => {
    let newPart = true
    let startIndex = 0
    const folderPathParts: string[] = []

    for (let i = 0; i < folderPath.length; i++) {
        if (newPart) {
            // If " is at the beginning, look ahead whether this is escape character or not.
            if (folderPath[i] === FOLDERS_SEPARATOR_ESCAPE_WRAPPER_CHARACTER) {
                // End of escaped string has to be in form of " at the end of path or "\ as end of single folder name.
                let escapedPartWasFound = false
                for (let j = i + 1; j < folderPath.length; j++) {
                    if (
                        j < folderPath.length - 1 &&
                        folderPath[j] === FOLDERS_SEPARATOR_ESCAPE_WRAPPER_CHARACTER &&
                        folderPath[j + 1] === FOLDERS_SEPARATOR_DISPLAY // case of "\
                    ) {
                        folderPathParts.push(folderPath.substring(i + 1, j))
                        i = j + 1
                        escapedPartWasFound = true
                        break
                    } else if (
                        j === folderPath.length - 1 &&
                        folderPath[j] === FOLDERS_SEPARATOR_ESCAPE_WRAPPER_CHARACTER
                    ) {
                        folderPathParts.push(folderPath.substring(i + 1, j))
                        i = j + 1
                        escapedPartWasFound = true
                        break
                    }
                }
                if (!escapedPartWasFound) {
                    newPart = false
                    startIndex = i
                }
            } else if (folderPath[i] !== FOLDERS_SEPARATOR_DISPLAY) {
                // New part can only start with a valid character (any char except separator or whitespace)
                newPart = false
                startIndex = i
                // This is both start of a part and end of the whole string, put the char as part of the path only if it is not a separator character.
                if (i === folderPath.length - 1 && folderPath[i] !== FOLDERS_SEPARATOR_DISPLAY) {
                    folderPathParts.push(folderPath.substring(startIndex, i + 1))
                }
            }
        } else if (folderPath[i] === FOLDERS_SEPARATOR_DISPLAY) {
            // If not starting new part, loop through all the chars until coming into \ or end of the string
            folderPathParts.push(folderPath.substring(startIndex, i)) // Separator char, add part without separator to the array
            newPart = true
        } else if (i === folderPath.length - 1) {
            // end of the string, push what is currently available
            folderPathParts.push(folderPath.substring(startIndex, i + 1))
        }
    }

    return folderPathParts
        .map((folderPathPart) => folderPathPart.trim()) // Trim whitespaces from start / end.
        .filter((folderPathPart) => folderPathPart.length > 0) // Ignore all parts that are only whitespaces.
        .join(FOLDERS_SEPARATOR_BACKEND)
}

/**
 * Prepares description value for report update or add operation - the description represents both folder path and actual reportName.
 *
 * @param folderPath Folder path provided by user.
 * @param reportName Report name provided by user.
 */
export const prepareReportDescriptionForAddUpdateRequest = (folderPath: string, reportName: string): string => {
    const parsedFolderPath = parseFolderPathFromUserAndTransformIntoValueForRequest(folderPath)
    if (parsedFolderPath) {
        return `${parsedFolderPath}${FOLDERS_SEPARATOR_BACKEND}${reportName}`
    }
    // There is no folder.
    return reportName
}

/**
 * Converts list of report items to tree structure so that it is possible to render reports and folders correctly.
 * @param reportListItemsAsFlatArray Array of menu items with links to parent item.
 * @param parentFolderID The parent item identifier.
 */
export const toReportListItemsAsTree = (
    reportListItemsAsFlatArray: ReportListItemType[],
    parentFolderID?: string
): ReportListItemType[] => {
    // Filters report items where parent identifier is equal to id provided as parameter.
    const filterReportListItemByParentID = (reportListItem: ReportListItemType): boolean =>
        reportListItem.parentFolderID === parentFolderID

    // Reducer function that builds tree structure.
    const reduceReportListItemWithSubItems = (
        currentLevelReportListItems: ReportListItemType[],
        iteratedReportListItem: ReportListItemType
    ): ReportListItemType[] => [
        ...currentLevelReportListItems,
        {
            ...iteratedReportListItem,
            subItems: toReportListItemsAsTree(reportListItemsAsFlatArray, iteratedReportListItem.id)
        }
    ]

    return reportListItemsAsFlatArray
        .filter(filterReportListItemByParentID)
        .reduce(reduceReportListItemWithSubItems, [])
}
